// AuthForm.js
import React, { useState } from 'react';
import { auth, db, googleProvider } from '../../firebase';
import { createUserWithEmailAndPassword, multiFactor, TotpMultiFactorGenerator, sendEmailVerification, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { collection, addDoc } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../images/images.png';
import QRCode from 'qrcode.react';

const normalizePhoneNumber = (phone) => {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('1')) return digits;
  return '1' + digits;
};

function AuthFormSearcher({ formData }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // New state for phone number
  const [capitalNeed, setCapitalNeed] = useState('debt_only');
  const [loading, setLoading] = useState(false);
  const [enrollTOTP, setEnrollTOTP] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showCalendlyPopup, setShowCalendlyPopup] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referringBrokerId = searchParams.get('brokerId')?.trim() || '';

  const sendSignUpNotification = async (userDetails) => {
    try {
      const emailRef = collection(db, 'email');
      
      const emailData = {
        to: "max@sbacapital.ai",
        from: "Isak @ Lended <isak@lended.ai>",
        replyTo: "isak@lended.ai",
        subject: "New Sign Up - Searcher",
        templateName: "SignUpNotification",
        message: {
          subject: "New Sign Up - Searcher",
          html: `
            <p>Hello Max,</p>
            <p>A new user has signed up as a Searcher:</p>
            <ul>
              <li>Name: ${userDetails.firstName} ${userDetails.lastName}</li>
              <li>Email: ${userDetails.email}</li>
              <li>Phone Number: ${userDetails.phoneNumber}</li>
              <li>Capital Need: ${userDetails.capitalNeed === 'debt_and_investors' ? 'Debt + Investors' : 'Debt Financing'}</li>
            </ul>
            <p>Best regards,<br>Lended AI System</p>
          `,
        },
      };

      await addDoc(emailRef, emailData);
      console.log('Sign-up notification email sent successfully');
    } catch (error) {
      console.error('Error sending sign-up notification email:', error);
    }
  };

  const handleEmailSignUp = async () => {
    if (!termsAccepted) {
      alert('You must agree to the terms of service.');
      return;
    }
    const hasGoogleSession = auth.currentUser?.email?.toLowerCase() === email.toLowerCase();
    if (email && (password || hasGoogleSession) && firstName && lastName && phoneNumber) {
      try {
        setLoading(true);
        const normalizedPhone = normalizePhoneNumber(phoneNumber);

        // Create user with email and password
        const userCredential = hasGoogleSession ? { user: auth.currentUser } : await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        console.log('User signed up:', user);

        // Create a user profile in 'users' node with 'Pre-lender' as the initial status
        const userProfileRef = doc(db, 'users', user.uid);
        await setDoc(userProfileRef, {
          email: user.email,
          firstName,
          lastName,
          phoneNumber: normalizedPhone,
          status: 'Pre-lender',
          capitalNeed,
          lastUserUpdate: new Date(),
          ...(referringBrokerId ? {
            brokerId: referringBrokerId,
            referredByBrokerId: referringBrokerId,
            referralSource: 'broker_invite',
          } : {}),
        });

        // Create an empty loan application with the user's name
        const loanApplicationRef = await addDoc(collection(db, 'loanApplications'), {
          action: `call ${firstName} to see what they are working on (new signup)`,
          called: false,
          allDocumentsUploaded: false,
          userId: user.uid,
          firstName,
          lastName,
          email,
          phoneNumber: normalizedPhone,
          businessName: '',
          businessAge: '',
          businessState: '',
          loanSize: '',
          industry: '',
          capTable: [],
          ebitda: '',
          revenue: '',
          additionalNotes: '',
          status: 'Pre-lender',
          createdAt: new Date(),
          updatedAt: new Date(),
          personalGuarantors: [{ firstName, lastName }],
          realEstateAppraisal: '0',
          lastUserUpdate: new Date(),
          loanPurpose: 'Acquisition',
          capitalNeed,
          engagementSigned: false,
          engagementPaid: false,
          ...(referringBrokerId ? {
            brokerId: referringBrokerId,
            referredByBrokerId: referringBrokerId,
            source: 'broker_referral',
          } : {}),
        });

        // Create a reference to this application under the 'applications' node within the 'users' node
        const userApplicationsRef = doc(db, 'users', user.uid, 'applications', loanApplicationRef.id);
        await setDoc(userApplicationsRef, {
          applicationID: loanApplicationRef.id,
          status: 'Pre-lender',
        });

        console.log('User profile and empty loan application created');

        // Migrate files from pending searcher uploads (if any were uploaded via shared link)
        try {
          const pendingDocsSnap = await getDocs(query(
            collection(db, 'documentMetadata'),
            where('email', '==', email.toLowerCase().trim()),
            where('uploadedBy', '==', 'shared_link')
          ));
          for (const docSnap of pendingDocsSnap.docs) {
            await updateDoc(doc(db, 'documentMetadata', docSnap.id), {
              loanApplicationId: loanApplicationRef.id,
              userId: user.uid,
              migratedFromPending: true,
              migratedAt: new Date().toISOString(),
            });
          }
          if (pendingDocsSnap.docs.length > 0) {
            console.log(`Migrated ${pendingDocsSnap.docs.length} files from pending uploads`);
          }
        } catch (migrationErr) {
          console.error('Error migrating pending files:', migrationErr);
        }

        // Send notification email to Max
        await sendSignUpNotification({
          firstName,
          lastName,
          email,
          phoneNumber: normalizedPhone,
          capitalNeed
        });

        // Send welcome email to the new searcher
        try {
          const welcomeEmailRef = collection(db, 'email');
          await addDoc(welcomeEmailRef, {
            to: email,
            from: "Isak @ Lended <isak@lended.ai>",
            replyTo: "isak@lended.ai",
            subject: "Welcome to Lended - Next Steps",
            templateName: "WelcomeSearcher",
            message: {
              subject: "Welcome to Lended - Next Steps",
              html: `
                <p>Hi ${firstName},</p>
                <p>Welcome to Lended! Your account has been created and you're all set to get started.</p>
                <p>Here's what happens next:</p>
                <ol>
                  <li>Upload your personal documents (tax returns, personal financial statement, etc.)</li>
                  <li>Once we have your documents, we'll get you prequalified within 24 hours</li>
                  <li>Start pursuing deals with your prequalification in hand</li>
                </ol>
                <p>You can log in anytime to upload documents and check your status:</p>
                <p><a href="https://lended.ai/login" style="display:inline-block;padding:10px 24px;background-color:#2563eb;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">Log In to Your Account</a></p>
                <p>I'm here to help with anything you need along the way. Don't hesitate to reach out!</p>
                <p>Best regards,<br>Isak<br>(914) 414-1106<br>Lended</p>
              `,
            },
          });
          console.log('Welcome email sent to new searcher');
        } catch (welcomeErr) {
          console.error('Error sending welcome email:', welcomeErr);
        }

        // Trigger LeadConnector webhook
        try {
          await fetch('https://services.leadconnectorhq.com/hooks/jt2cla2yKK13pavtLwNJ/webhook-trigger/cd3c480a-a7b5-40fe-a1a5-07f38b4934ec', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              firstName,
              lastName,
              email,
              phone: normalizedPhone,
            }),
          });
          console.log('LeadConnector webhook triggered');
        } catch (webhookErr) {
          console.error('Error triggering webhook:', webhookErr);
        }

        if (enrollTOTP) {
          // Send email verification if TOTP is enabled
          await sendEmailVerification(user);
          alert('A verification email has been sent to your email address. Please verify your email to complete the sign-up process.');
          setEmailSent(true);
        } else {
          // Show Calendly popup before navigating to portal
          setLoading(false);
          setShowCalendlyPopup(true);
        }

        setLoading(false);
      } catch (error) {
        console.log('Error signing up:', error);
        setLoading(false);

        if (error.code === 'auth/email-already-in-use') {
          alert('The email address is already in use. Please use a different email.');
        } else {
          alert('Error signing up. Please try again.');
        }
      }
    } else {
      alert('Please enter all required fields.');
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      const { user } = await signInWithPopup(auth, googleProvider);
      const parts = (user.displayName || '').trim().split(/\s+/);
      setEmail(user.email || '');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') alert('Google sign-up could not be completed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle email verification status check
  const checkEmailVerification = async () => {
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        console.log('Email verified');
        setEmailVerified(true);
        // Generate a TOTP secret
        const multiFactorSession = await multiFactor(auth.currentUser).getSession();
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
        setTotpSecret(totpSecret);
      } else {
        alert('Please verify your email before proceeding.');
      }
    } catch (error) {
      console.log('Error checking email verification:', error);
      alert('Error verifying email. Please try again.');
    }
  };

  const handleTOTPEnrollment = async () => {
    try {
      // Finalize the enrollment
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode
      );
      await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'TOTP');
      console.log('TOTP enrollment successful');

      // Delay the navigation to the portal to allow data to be fully updated
      setTimeout(() => {
        setLoading(false);
        navigate('/portal', { state: { retryCount: 0 } });
      }, 2000); // Adjust the delay as needed
    } catch (error) {
      console.log('Error enrolling TOTP:', error);
      alert('Invalid verification code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-blue-900 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-white p-10 rounded-xl shadow-2xl">
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src={logo}
            alt="Your Company"
          />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create an account to get a quick pre-check on your financials, enabling you to receive term sheets within hours when you find a business.
          </p>
        </div>
        {!emailSent ? (
          <div className="space-y-6">
            <button type="button" onClick={handleGoogleSignUp} disabled={loading} className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60">
              <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.06H12v3.9h5.38a4.6 4.6 0 0 1-2 3.02v2.53h3.24c1.9-1.75 2.98-4.33 2.98-7.39Z"/><path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.38l-3.24-2.53c-.9.6-2.05.96-3.38.96-2.6 0-4.81-1.76-5.6-4.12H3.05v2.6A10 10 0 0 0 12 22Z"/><path fill="#FBBC05" d="M6.4 13.93A6 6 0 0 1 6.09 12c0-.67.12-1.32.31-1.93v-2.6H3.05A10 10 0 0 0 2 12c0 1.61.39 3.14 1.05 4.53l3.35-2.6Z"/><path fill="#EA4335" d="M12 5.95c1.47 0 2.79.5 3.82 1.49l2.87-2.87A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.95 5.47l3.35 2.6c.79-2.36 3-4.12 5.6-4.12Z"/></svg>
              Continue with Google
            </button>
            <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-500">then confirm your details</span></div></div>
            <div className="rounded-md shadow-sm space-y-4">
              <div className="flex space-x-4">
                <div className="w-1/2 relative">
                  <label
                    htmlFor="firstName"
                    className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="John"
                  />
                </div>
                <div className="w-1/2 relative">
                  <label
                    htmlFor="lastName"
                    className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="relative">
                <label
                  htmlFor="phoneNumber"
                  className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="(123) 456-7890"
                />
              </div>
              <div className="relative">
                <label
                  htmlFor="email"
                  className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                >
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="you@example.com"
                />
              </div>
              <fieldset className="rounded-xl border border-gray-200 p-3">
                <legend className="px-1 text-xs font-semibold text-gray-700">What capital should we help you prepare?</legend>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <label className={`cursor-pointer rounded-lg border p-3 text-sm ${capitalNeed === 'debt_only' ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 text-gray-600'}`}>
                    <input type="radio" name="capitalNeed" value="debt_only" checked={capitalNeed === 'debt_only'} onChange={(e) => setCapitalNeed(e.target.value)} className="mr-2" />
                    Debt
                  </label>
                  <label className={`cursor-pointer rounded-lg border p-3 text-sm ${capitalNeed === 'debt_and_investors' ? 'border-violet-500 bg-violet-50 text-violet-900' : 'border-gray-200 text-gray-600'}`}>
                    <input type="radio" name="capitalNeed" value="debt_and_investors" checked={capitalNeed === 'debt_and_investors'} onChange={(e) => setCapitalNeed(e.target.value)} className="mr-2" />
                    Debt + investors
                  </label>
                </div>
              </fieldset>
              <div className="relative">
                <label
                  htmlFor="password"
                  className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center">
                <input
                  id="enrollTOTP"
                  name="enrollTOTP"
                  type="checkbox"
                  checked={enrollTOTP}
                  onChange={(e) => setEnrollTOTP(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enrollTOTP" className="ml-2 block text-sm text-gray-900">
                  Enable Two-Factor Authentication using Google Authenticator (TOTP)
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="termsAccepted" className="ml-2 block text-sm text-gray-900">
                  I agree to the <button type="button" onClick={() => setShowTerms(true)} className="text-blue-600 underline">terms of service</button>
                </label>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleEmailSignUp}
                  disabled={loading}
                  className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                    loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  }`}
                >
                  {loading ? 'Creating account...' : 'Create an account'}
                </button>
              </div>
            </div>
          </div>
        ) : enrollTOTP && !emailVerified ? (
          <div className="space-y-6">
            <h3 className="text-center text-xl font-bold tracking-tight text-gray-900">
              Check your email to verify your account.
            </h3>
            <p className="text-center text-sm text-gray-600">
              After verifying your email, click the button below to complete the registration process.
            </p>
            <div>
              <button
                type="button"
                onClick={checkEmailVerification}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Once you have verified your email click here
              </button>
            </div>
          </div>
        ) : enrollTOTP && totpSecret ? (
          <div>
            <h3 className="mt-6 text-center text-xl font-bold tracking-tight text-gray-900">
              Two-Factor Authentication Setup
            </h3>
            <div className="mt-4">
              <ul className="list-disc list-inside text-sm text-gray-500">
                <li>Download the Google Authenticator app on your phone</li>
                <li>Click the + in the bottom right corner and scan the QR code below with your authenticator app or enter the secret key manually.</li>
                <li>Input the verification code displayed in the app</li>
              </ul>
              <div className="mt-4 flex justify-center">
                <QRCode value={totpSecret.generateQrCodeUrl(email, 'Your App Name')} />
              </div>
              <div className="mt-4">
                <label htmlFor="secretKey" className="block text-sm font-medium text-gray-700">
                  Secret Key
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="secretKey"
                    value={totpSecret.secretKey}
                    readOnly
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Enter the code from your authenticator app"
                  />
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleTOTPEnrollment}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Verify and Enroll
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <h3 className="text-center text-xl font-bold tracking-tight text-gray-900">
              Registration Complete
            </h3>
            <p className="text-center text-sm text-gray-600">
              Your account has been successfully verified and set up. You can now access the portal.
            </p>
            <div>
              <button
                type="button"
                onClick={() => navigate('/portal', { state: { retryCount: 0 } })}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Portal
              </button>
            </div>
          </div>
        )}
      </div>
      {showTerms && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Terms of Service</h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p>Effective Date: {new Date().toLocaleDateString()}</p>
              <p>
                By using our services, you agree that we may receive referral compensation for matching you with a bank. This compensation helps us maintain and improve our services.
              </p>
              <p>
                We are committed to providing you with the best possible experience and will always strive to act in your best interest.
              </p>
              <p>
                Your information will only be shared with potential lenders and nobody else. We respect your privacy and maintain strict confidentiality of your data.
              </p>
              <p>
                For any questions or concerns regarding these terms, please contact:
              </p>
              <p>
                Max Krakow, CEO<br />
                Phone: (914) 319-7160
              </p>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showCalendlyPopup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4" style={{ maxHeight: '90vh' }}>
            <h2 className="text-xl font-bold mb-2 text-center">Account Created Successfully!</h2>
            <p className="text-center text-gray-600 mb-4">
              Book a free consultation to discuss your SBA loan needs with our team.
            </p>
            <div className="w-full" style={{ height: '500px' }}>
              <iframe
                src="https://calendly.com/isak-lended/lended-sba-financing-intro-call"
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule a consultation"
              ></iframe>
            </div>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => navigate('/portal', { state: { retryCount: 0 } })}
                className="text-gray-600 hover:text-gray-800 underline text-sm"
              >
                Skip for now and go to Portal →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuthFormSearcher;
